import React, { useEffect, useMemo, useState } from 'react';

import {
    createAcceptanceTerritory,
    downloadAcceptanceTemplate,
    downloadFileByUrl,
    importAcceptanceCassettes,
    importAcceptanceModules,
    loadAcceptanceMetadata,
} from './api';
import SearchableSelect from './SearchableSelect';


const fallbackMetadata = {
    locations: [
        { id: 1, city: 'Тюмень', address: 'Локальный пример' },
        { id: 2, city: 'Москва', address: 'Локальный пример' },
    ],
    territories: [],
    repair_statuses: [
        { value: 'Ожидает ремонт', label: 'Ожидает ремонт' },
        { value: 'Готов', label: 'Готов' },
        { value: 'МСК', label: 'МСК' },
        { value: 'ТМН', label: 'ТМН' },
        { value: 'ХАБ', label: 'ХАБ' },
        { value: 'Отказ', label: 'Отказ' },
        { value: 'Подменный', label: 'Подменный' },
    ],
};

const initialModuleForm = {
    repair_status: 'Ожидает ремонт',
    file: null,
};

const initialCassetteForm = {
    territory_id: '',
    number_kits: '',
    repair_status: 'Ожидает ремонт',
    file: null,
};

const initialTerritoryForm = {
    territory: '',
    address: '',
};


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function SummaryCard({ label, value }) {
    return (
        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}


function buildInvalidReasonSummary(items) {
    const counts = new Map();
    items.forEach((item) => {
        const note = item.note || 'Без описания';
        counts.set(note, (counts.get(note) || 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([note, count]) => ({ note, count }))
        .sort((left, right) => right.count - left.count || left.note.localeCompare(right.note, 'ru'));
}


function InvalidItemsTable({ items }) {
    if (!items.length) {
        return <div className="shell-emptyState">Ошибок нет.</div>;
    }

    return (
        <div className="shell-tableWrap">
            <table className="shell-table">
                <thead>
                    <tr>
                        <th>Серийный номер</th>
                        <th>Локация</th>
                        <th>Дата</th>
                        <th>Кто принял</th>
                        <th>Примечание</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={`${item.serial_number}-${index}`} className="shell-tableRow">
                            <td>{item.serial_number || '—'}</td>
                            <td>{item.location || '—'}</td>
                            <td>{item.arrival_date || '—'}</td>
                            <td>{item.accepted_user || '—'}</td>
                            <td>{item.note || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function buildImportHints(label) {
    return label === 'Кассеты'
        ? [
            'Проверь маску серийного номера, территорию КИЦ и номер заявки КИЦ.',
            'Если КИЦ и номер заявки одинаковые для всего файла, можно задать их сверху вручную.',
        ]
        : [
            'Проверь маску серийного номера, дату оприходования и текущий статус оборудования в РЦ.',
        ];
}


function AcceptanceResultSummary({ result, onOpen, onDownload }) {
    if (!result || !result.invalid_count) {
        return null;
    }

    return (
        <div className="shell-acceptanceResultSummary">
            <div className="shell-acceptanceResultSummaryMeta">
                <span>Последний импорт</span>
                <strong>{result.label}</strong>
                <small>
                    Создано: {result.created_count} • Ошибок: {result.invalid_count}
                </small>
            </div>
            <div className="shell-requestActionButtons">
                <button
                    type="button"
                    className="shell-actionButton shell-actionButton--secondary"
                    onClick={onOpen}
                >
                    Посмотреть ошибки
                </button>
                <button
                    type="button"
                    className="shell-actionButton shell-actionButton--secondary"
                    onClick={onDownload}
                >
                    Скачать список
                </button>
            </div>
        </div>
    );
}


function AcceptanceResultModal({ result, onClose, onDownload }) {
    if (!result || !result.invalid_count) {
        return null;
    }

    const previewItems = result.invalid_items_preview || [];
    const reasonSummary = result.invalid_reason_summary?.length
        ? result.invalid_reason_summary
        : buildInvalidReasonSummary(previewItems);
    const importHints = buildImportHints(result.label);

    return (
        <div className="shell-modalBackdrop" onClick={onClose}>
            <div className="shell-modalCard shell-modalCard--wide" onClick={(event) => event.stopPropagation()}>
                <div className="shell-modalHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Импорт с ошибками</span>
                        <h3>{result.label}: проблемные строки</h3>
                    </div>
                    <div className="shell-requestActionButtons">
                        <button
                            type="button"
                            className="shell-actionButton shell-actionButton--secondary"
                            onClick={onDownload}
                            disabled={!result.error_report_url}
                        >
                            Скачать Excel
                        </button>
                        <button type="button" className="shell-modalClose" onClick={onClose}>
                            Закрыть
                        </button>
                    </div>
                </div>

                <div className="shell-modalBody shell-modalBody--acceptance">
                    <div className="shell-acceptanceResult">
                    <div className="shell-acceptanceResultGrid">
                        <div className="shell-requestDetailItem">
                            <span>Режим</span>
                            <strong>{result.label}</strong>
                        </div>
                        <div className="shell-requestDetailItem">
                            <span>Создано</span>
                            <strong>{result.created_count}</strong>
                        </div>
                        <div className="shell-requestDetailItem">
                            <span>Ошибок</span>
                            <strong>{result.invalid_count}</strong>
                        </div>
                    </div>

                        <p className="shell-cardHint">
                            <strong>Что проверять:</strong> {importHints.join(' ')}
                        </p>

                        <div className="shell-requestActionCard shell-requestActionCard--flat">
                            <span className="shell-cardLabel">Причины ошибок</span>
                            <div className="shell-reasonList">
                                {reasonSummary.map((item) => (
                                    <div key={item.note} className="shell-reasonItem">
                                        <span className="shell-reasonBadge">
                                            {item.count} {item.count === 1 ? 'строка' : item.count < 5 ? 'строки' : 'строк'}
                                        </span>
                                        <strong>{item.note}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {result.invalid_items_truncated ? (
                            <div className="shell-inlineMessage shell-inlineMessage--warning">
                                Показаны первые {result.invalid_items_preview_count} строк. Полный список скачай отдельным Excel-файлом.
                            </div>
                        ) : null}

                        <InvalidItemsTable items={previewItems} />
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function AcceptancePage() {
    const [activeTab, setActiveTab] = useState('modules');
    const [metadata, setMetadata] = useState(fallbackMetadata);
    const [metadataWarning, setMetadataWarning] = useState('');
    const [modulesForm, setModulesForm] = useState(initialModuleForm);
    const [cassettesForm, setCassettesForm] = useState(initialCassetteForm);
    const [territoryForm, setTerritoryForm] = useState(initialTerritoryForm);
    const [territoryMessage, setTerritoryMessage] = useState('');
    const [territoryError, setTerritoryError] = useState('');
    const [submitState, setSubmitState] = useState({ mode: '', loading: false, error: '' });
    const [result, setResult] = useState(null);
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [toast, setToast] = useState(null);
    const [moduleFileKey, setModuleFileKey] = useState(0);
    const [cassetteFileKey, setCassetteFileKey] = useState(0);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const payload = await loadAcceptanceMetadata();
                if (!cancelled) {
                    setMetadata(payload);
                    setModulesForm((current) => ({
                        ...current,
                        repair_status: current.repair_status || 'Ожидает ремонт',
                    }));
                    setCassettesForm((current) => ({
                        ...current,
                        repair_status: current.repair_status || 'Ожидает ремонт',
                    }));
                    setMetadataWarning('');
                }
            } catch (_error) {
                if (!cancelled) {
                    setMetadata(fallbackMetadata);
                    setMetadataWarning('Не удалось загрузить справочники. Показан локальный пример.');
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }

        const timer = window.setTimeout(() => setToast(null), 4000);
        return () => window.clearTimeout(timer);
    }, [toast]);

    const summary = useMemo(() => ([
        { label: 'Текущая локация', value: metadata.current_location?.city || 'Не определена' },
        { label: 'Территорий', value: metadata.territories.length },
        { label: 'Статусов', value: metadata.repair_statuses.length },
    ]), [metadata]);

    const territoryOptions = metadata.territories || [];
    const repairStatusOptions = metadata.repair_statuses || [];

    async function handleInvalidItemsDownload(currentResult) {
        if (!currentResult?.error_report_url) {
            return;
        }

        try {
            await downloadFileByUrl(
                currentResult.error_report_url,
                `hyosung_acceptance_errors_${(currentResult.label || 'items').toLowerCase()}.xlsx`,
            );
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать список ошибок.');
            setToast({ type: 'error', text: message });
        }
    }

    function updateModuleField(name, value) {
        setModulesForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function updateCassetteField(name, value) {
        setCassettesForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function updateTerritoryField(name, value) {
        setTerritoryForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    async function handleTemplateDownload(kind) {
        try {
            await downloadAcceptanceTemplate(kind);
        } catch (error) {
            setSubmitState({
                mode: '',
                loading: false,
                error: extractErrorMessage(error, 'Не удалось скачать шаблон.'),
            });
        }
    }

    async function submitModules(event) {
        event.preventDefault();
        setSubmitState({ mode: 'modules', loading: true, error: '' });

        const formData = new FormData();
        formData.append('repair_status', modulesForm.repair_status);
        if (modulesForm.file) {
            formData.append('file', modulesForm.file);
        }

        try {
            const payload = await importAcceptanceModules(formData);
            const nextResult = payload;
            setResult(nextResult);
            setModulesForm({
                ...initialModuleForm,
                repair_status: 'Ожидает ремонт',
            });
            setModuleFileKey((value) => value + 1);
            setSubmitState({ mode: '', loading: false, error: '' });
            if (nextResult.invalid_count > 0) {
                setResultModalOpen(true);
                setToast({
                    type: 'error',
                    text: `Импорт модулей завершен с ошибками: создано ${nextResult.created_count}, проблемных строк ${nextResult.invalid_count}.`,
                });
            } else {
                setResultModalOpen(false);
                setToast({
                    type: 'success',
                    text: `Модули оприходованы: создано ${nextResult.created_count}.`,
                });
            }
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось выполнить импорт модулей.');
            setSubmitState({
                mode: '',
                loading: false,
                error: message,
            });
            setToast({ type: 'error', text: message });
        }
    }

    async function submitCassettes(event) {
        event.preventDefault();
        setSubmitState({ mode: 'cassettes', loading: true, error: '' });

        const formData = new FormData();
        formData.append('repair_status', cassettesForm.repair_status);
        formData.append('territory_id', cassettesForm.territory_id);
        formData.append('number_kits', cassettesForm.number_kits);
        if (cassettesForm.file) {
            formData.append('file', cassettesForm.file);
        }

        try {
            const payload = await importAcceptanceCassettes(formData);
            const nextResult = payload;
            setResult(nextResult);
            setCassettesForm({
                ...initialCassetteForm,
                repair_status: 'Ожидает ремонт',
            });
            setCassetteFileKey((value) => value + 1);
            setSubmitState({ mode: '', loading: false, error: '' });
            if (nextResult.invalid_count > 0) {
                setResultModalOpen(true);
                setToast({
                    type: 'error',
                    text: `Импорт кассет завершен с ошибками: создано ${nextResult.created_count}, проблемных строк ${nextResult.invalid_count}.`,
                });
            } else {
                setResultModalOpen(false);
                setToast({
                    type: 'success',
                    text: `Кассеты оприходованы: создано ${nextResult.created_count}.`,
                });
            }
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось выполнить импорт кассет.');
            setSubmitState({
                mode: '',
                loading: false,
                error: message,
            });
            setToast({ type: 'error', text: message });
        }
    }

    async function submitTerritory(event) {
        event.preventDefault();
        setTerritoryMessage('');
        setTerritoryError('');

        try {
            const payload = await createAcceptanceTerritory(territoryForm);
            const territory = payload.territory;
            setMetadata((current) => ({
                ...current,
                territories: [...current.territories, territory].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
            }));
            setCassettesForm((current) => ({
                ...current,
                territory_id: String(territory.id),
            }));
            setTerritoryForm(initialTerritoryForm);
            setTerritoryMessage('Территория добавлена.');
        } catch (error) {
            setTerritoryError(extractErrorMessage(error, 'Не удалось добавить территорию.'));
        }
    }

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}
            {resultModalOpen ? (
                <AcceptanceResultModal
                    result={result}
                    onClose={() => setResultModalOpen(false)}
                    onDownload={() => handleInvalidItemsDownload(result)}
                />
            ) : null}
            <section className="shell-section shell-section--acceptanceImport">
                {metadataWarning ? <div className="shell-banner">{metadataWarning}</div> : null}
                {submitState.error ? <div className="shell-banner">{submitState.error}</div> : null}

                <div className="shell-inlineStack">
                        <div className="shell-sectionHeader shell-sectionHeader--nested">
                            <div>
                                <h3>Приемка</h3>
                            </div>
                            <div className="shell-pageSummary shell-pageSummary--toolbar">
                                {summary.map((item) => (
                                    <SummaryCard key={item.label} label={item.label} value={item.value} />
                                ))}
                            </div>
                        </div>

                        <div className="shell-sectionHeader shell-sectionHeader--nested">
                            <div>
                                <h3>Импорт оборудования</h3>
                            </div>
                            <AcceptanceResultSummary
                                result={result}
                                onOpen={() => setResultModalOpen(true)}
                                onDownload={() => handleInvalidItemsDownload(result)}
                            />
                        </div>

                        <div className="shell-tabRow">
                            <button
                                type="button"
                                className={`shell-tabButton${activeTab === 'modules' ? ' is-active' : ''}`}
                                onClick={() => setActiveTab('modules')}
                            >
                                Модули
                            </button>
                            <button
                                type="button"
                                className={`shell-tabButton${activeTab === 'cassettes' ? ' is-active' : ''}`}
                                onClick={() => setActiveTab('cassettes')}
                            >
                                Кассеты
                            </button>
                        </div>

                        {activeTab === 'modules' ? (
                            <form className="shell-searchForm shell-searchForm--compact" onSubmit={submitModules}>
                                <label className="shell-field">
                                    <span>Статус ремонта</span>
                                    <SearchableSelect
                                        value={modulesForm.repair_status}
                                        onChange={(value) => updateModuleField('repair_status', value)}
                                        options={repairStatusOptions}
                                        placeholder="Выбрать"
                                        disabled={submitState.loading}
                                    />
                                </label>

                                <label className="shell-field">
                                    <span>Excel</span>
                                    <input
                                        key={moduleFileKey}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={(event) => updateModuleField('file', event.target.files?.[0] || null)}
                                        disabled={submitState.loading}
                                    />
                                </label>

                                <div className="shell-searchActions">
                                    <button
                                        type="submit"
                                        className="shell-actionButton shell-actionButton--primary"
                                        disabled={submitState.loading}
                                    >
                                        {submitState.loading && submitState.mode === 'modules' ? 'Импорт...' : 'Оприходовать модули'}
                                    </button>
                                    <button
                                        type="button"
                                        className="shell-actionButton shell-actionButton--secondary"
                                        onClick={() => handleTemplateDownload('modules')}
                                        disabled={submitState.loading}
                                    >
                                        Скачать шаблон
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <form className="shell-searchForm shell-searchForm--compact shell-searchForm--acceptanceCassette" onSubmit={submitCassettes}>
                                    <label className="shell-field shell-field--territorySelect">
                                        <span>Территория</span>
                                        <SearchableSelect
                                            value={cassettesForm.territory_id}
                                            onChange={(value) => updateCassetteField('territory_id', value)}
                                            options={territoryOptions.map((territory) => ({
                                                value: String(territory.id),
                                                label: territory.name,
                                                meta: territory.address || '',
                                            }))}
                                            placeholder="Определить из файла"
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <label className="shell-field">
                                        <span>Номер заявки КИЦ</span>
                                        <input
                                            type="number"
                                            value={cassettesForm.number_kits}
                                            onChange={(event) => updateCassetteField('number_kits', event.target.value)}
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <label className="shell-field">
                                        <span>Статус ремонта</span>
                                        <SearchableSelect
                                            value={cassettesForm.repair_status}
                                            onChange={(value) => updateCassetteField('repair_status', value)}
                                            options={repairStatusOptions}
                                            placeholder="Выбрать"
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <label className="shell-field shell-field--full">
                                        <span>Excel</span>
                                        <input
                                            key={cassetteFileKey}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={(event) => updateCassetteField('file', event.target.files?.[0] || null)}
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <div className="shell-searchActions">
                                        <button
                                            type="submit"
                                            className="shell-actionButton shell-actionButton--primary"
                                            disabled={submitState.loading}
                                        >
                                            {submitState.loading && submitState.mode === 'cassettes' ? 'Импорт...' : 'Оприходовать кассеты'}
                                        </button>
                                        <button
                                            type="button"
                                            className="shell-actionButton shell-actionButton--secondary"
                                            onClick={() => handleTemplateDownload('cassettes')}
                                            disabled={submitState.loading}
                                        >
                                            Скачать шаблон
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                </div>
            </section>

            {activeTab === 'cassettes' ? (
                <section className="shell-section shell-section--embedded shell-section--acceptanceTerritory">
                    <div className="shell-sectionHeader shell-sectionHeader--nested">
                        <div>
                            <span className="shell-sectionEyebrow">Справочник</span>
                            <h3>Новая территория</h3>
                        </div>
                    </div>

                    <form className="shell-searchForm shell-searchForm--compact shell-searchForm--territory" onSubmit={submitTerritory}>
                                    <label className="shell-field">
                                        <span>Название</span>
                                        <input
                                            type="text"
                                            value={territoryForm.territory}
                                            onChange={(event) => updateTerritoryField('territory', event.target.value)}
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <label className="shell-field shell-field--full">
                                        <span>Адрес</span>
                                        <input
                                            type="text"
                                            value={territoryForm.address}
                                            onChange={(event) => updateTerritoryField('address', event.target.value)}
                                            disabled={submitState.loading}
                                        />
                                    </label>

                                    <div className="shell-searchActions">
                                        <button type="submit" className="shell-actionButton shell-actionButton--secondary">
                                            Добавить территорию
                                        </button>
                                        {territoryMessage ? <div className="shell-inlineMessage">{territoryMessage}</div> : null}
                                        {territoryError ? <div className="shell-inlineMessage shell-inlineMessage--error">{territoryError}</div> : null}
                                    </div>
                    </form>
                </section>
            ) : null}
        </div>
    );
}
